import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { FaArrowLeft, FaRoute, FaSearch } from 'react-icons/fa';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { isKickoffEligibleClient } from '../../utils/crmClientListFilters';
import { fetchAdditionalTeamMemberNamesByAccountIds } from '../../services/accountTeamMembersService';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';

function roadmapRowsFromQuerySnapshot(querySnapshot) {
  const roadmaps = [];
  for (const accountDoc of querySnapshot.docs) {
    const data = accountDoc.data();
    if (!data.roadmapTemplates || !Array.isArray(data.roadmapTemplates) || data.roadmapTemplates.length === 0) {
      continue;
    }
    if (!isKickoffEligibleClient(data)) continue;

    const contact = data.contact || {};
    const contactName =
      contact.name ||
      (contact.firstName && contact.lastName
        ? `${contact.firstName} ${contact.lastName}`
        : contact.firstName || contact.lastName || 'No contact name');

    const totalTemplates = data.roadmapTemplates.length;
    const completedTemplates = data.roadmapTemplates.filter((t) => t.status === 'completed').length;
    const progress = totalTemplates > 0 ? Math.round((completedTemplates / totalTemplates) * 100) : 0;

    roadmaps.push({
      id: accountDoc.id,
      company: data.company || 'No company',
      contactName,
      contactEmail: String(contact.email || data.email || '').trim(),
      progress,
    });
  }
  roadmaps.sort((a, b) => a.company.localeCompare(b.company, undefined, { sensitivity: 'base' }));
  return roadmaps;
}

const ClientRoadmaps = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [clientRoadmaps, setClientRoadmaps] = useState([]);
  const [roadmapSearchQuery, setRoadmapSearchQuery] = useState('');
  const [loadingRoadmaps, setLoadingRoadmaps] = useState(true);

  useEffect(() => {
    const loadClientRoadmaps = async () => {
      setLoadingRoadmaps(true);
      try {
        const accountsRef = collection(db, 'accounts');
        const querySnapshot = await getDocs(accountsRef);
        const rows = roadmapRowsFromQuerySnapshot(querySnapshot);
        let enriched = rows;
        try {
          const primaryById = new Map(
            rows.map((r) => [r.id, { contactName: r.contactName, contactEmail: r.contactEmail }])
          );
          const teamMap = await fetchAdditionalTeamMemberNamesByAccountIds(primaryById);
          enriched = rows.map((r) => ({
            id: r.id,
            company: r.company,
            contactName: r.contactName,
            progress: r.progress,
            additionalTeamNames: teamMap.get(r.id) ?? [],
          }));
        } catch (teamErr) {
          console.warn('Could not load team members for roadmaps list:', teamErr);
          enriched = rows.map((r) => ({
            id: r.id,
            company: r.company,
            contactName: r.contactName,
            progress: r.progress,
            additionalTeamNames: [],
          }));
        }
        setClientRoadmaps(enriched);
      } catch (error) {
        console.error('Error loading client roadmaps:', error);
        setClientRoadmaps([]);
      } finally {
        setLoadingRoadmaps(false);
      }
    };

    loadClientRoadmaps();
  }, []);

  const filteredRoadmaps = useMemo(() => {
    const q = roadmapSearchQuery.trim().toLowerCase();
    if (!q) return clientRoadmaps;
    return clientRoadmaps.filter((r) => {
      const company = String(r.company || '').toLowerCase();
      const name = String(r.contactName || '').toLowerCase();
      const team = (r.additionalTeamNames || []).join(' ').toLowerCase();
      return company.includes(q) || name.includes(q) || team.includes(q);
    });
  }, [clientRoadmaps, roadmapSearchQuery]);

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-center relative">
            <button
              onClick={() => navigate('/crm/fulfillment')}
              className={`absolute left-0 flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                darkMode
                  ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
              title="Back to Fulfillment"
            >
              <FaArrowLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">Client Roadmaps</h1>
              <p className="text-gray-600 dark:text-gray-300">Manage client roadmaps and planning</p>
            </div>
          </div>
        </div>

        {!loadingRoadmaps && clientRoadmaps.length > 0 && (
          <div className="mb-6 max-w-xl mx-auto">
            <label htmlFor="roadmaps-client-search" className="sr-only">
              Search by company or client name
            </label>
            <div className="relative">
              <FaSearch
                className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                aria-hidden
              />
              <input
                id="roadmaps-client-search"
                type="search"
                value={roadmapSearchQuery}
                onChange={(e) => setRoadmapSearchQuery(e.target.value)}
                placeholder="Search company or client name…"
                autoComplete="off"
                className={`w-full rounded-lg border py-2.5 pl-10 pr-4 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  darkMode
                    ? 'border-gray-600 bg-gray-800 text-white placeholder:text-gray-500'
                    : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'
                }`}
              />
            </div>
          </div>
        )}

        {loadingRoadmaps ? (
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-12 text-center`}>
            <BBALoader size={48} className="mx-auto" />
            <p className="text-gray-600 dark:text-gray-400 mt-4">Loading client roadmaps...</p>
          </div>
        ) : clientRoadmaps.length > 0 ? (
          filteredRoadmaps.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredRoadmaps.map((roadmap) => (
                <div
                  key={roadmap.id}
                  onClick={() => navigate(`/crm/fulfillment/clientroadmaps/${roadmap.id}`)}
                  className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm border-l-4 border-l-blue-500 p-6 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all`}
                >
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{roadmap.company}</h3>
                  <div className="mb-4">
                    <p className="text-gray-600 dark:text-gray-400">{roadmap.contactName}</p>
                    {roadmap.additionalTeamNames?.length ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {roadmap.additionalTeamNames.join(', ')}
                      </p>
                    ) : null}
                  </div>

                  <div className="mb-2">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Progress</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{roadmap.progress}%</span>
                    </div>
                    <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                        style={{ width: `${roadmap.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-12 text-center`}>
              <FaSearch className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">No matches</h2>
              <p className="text-gray-600 dark:text-gray-400">
                Nothing matched "{roadmapSearchQuery.trim()}". Try another company or client name.
              </p>
            </div>
          )
        ) : (
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-12 text-center`}>
            <FaRoute className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">No Client Roadmaps</h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              No eligible clients with roadmaps (Program & Billing Active, program tier not Lost).
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientRoadmaps;
